// Local-development stand-in for the mobile app's GPS stream. Moves trucks back
// and forth between each job's load and dump sites so the Live Map can be built
// and demoed without live trucks on the road. Nothing here writes to Firestore.
import {
  LatLngLiteral,
  getBearingDegrees,
  getDistanceMeters,
} from '@/components/LiveMap/geo';
import {
  GeotrackPoint,
  LiveMapJob,
  LiveMapOperator,
} from '@/components/LiveMap/liveMapModel';

const METERS_PER_DEGREE_LAT = 111320;

// Simulated clock runs this many times faster than real time so trucks visibly move
export const SIMULATION_SPEEDUP = 10;

export type SimulatedTruckPlan = {
  uid: string;
  jobId: string;
  load: LatLngLiteral;
  dump: LatLngLiteral;
  laneOffsetMeters: number; // Bends each leg to its own right so outbound and return lanes separate
  speedMps: number;
  loadDwellMs: number;
  dumpDwellMs: number;
  phaseMs: number; // Where in the cycle this truck starts
  parkingJitterMeters: { east: number; north: number }; // Keeps trucks at the same site from stacking exactly
};

/**
 * Deterministic pseudo-random generator (mulberry32) seeded from a string, so a
 * given truck always gets the same plan across reloads.
 * @param {string} seedText
 * @returns {() => number} Function returning numbers in [0, 1).
 */
export function createSeededRandom(seedText: string): () => number {
  let seed = 0;
  for (let idx = 0; idx < seedText.length; idx++) {
    seed = (Math.imul(31, seed) + seedText.charCodeAt(idx)) | 0;
  }
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Move a point by a number of meters east and north.
 * @param {LatLngLiteral} point
 * @param {number} eastMeters
 * @param {number} northMeters
 * @returns {LatLngLiteral}
 */
export function offsetLatLng(
  point: LatLngLiteral,
  eastMeters: number,
  northMeters: number
): LatLngLiteral {
  return {
    lat: point.lat + northMeters / METERS_PER_DEGREE_LAT,
    lng:
      point.lng +
      eastMeters /
        (METERS_PER_DEGREE_LAT * Math.cos((point.lat * Math.PI) / 180)),
  };
}

/**
 * Point along a leg drawn as a quadratic curve that bows to the right of the
 * direction of travel by `offsetMeters` at its midpoint.
 * @param {LatLngLiteral} from
 * @param {LatLngLiteral} to
 * @param {number} offsetMeters
 * @param {number} fraction - 0 at `from`, 1 at `to`.
 * @returns {LatLngLiteral}
 */
export function getPointOnLeg(
  from: LatLngLiteral,
  to: LatLngLiteral,
  offsetMeters: number,
  fraction: number
): LatLngLiteral {
  const cosLat = Math.cos((from.lat * Math.PI) / 180);
  const east = (to.lng - from.lng) * METERS_PER_DEGREE_LAT * cosLat;
  const north = (to.lat - from.lat) * METERS_PER_DEGREE_LAT;
  const length = Math.hypot(east, north) || 1;
  // Right-hand perpendicular of the travel direction; the control point sits at
  // twice the offset because a quadratic curve only reaches half way to it.
  const midpoint = offsetLatLng(from, east / 2, north / 2);
  const control = offsetLatLng(
    midpoint,
    (north / length) * offsetMeters * 2,
    (-east / length) * offsetMeters * 2
  );
  const t = fraction;
  const u = 1 - t;
  return {
    lat: u * u * from.lat + 2 * u * t * control.lat + t * t * to.lat,
    lng: u * u * from.lng + 2 * u * t * control.lng + t * t * to.lng,
  };
}

/**
 * Plan a looping haul for a truck on a job. Jobs with a single site get a
 * made-up origin a few miles away so the truck still has somewhere to come from.
 * @param {string} uid
 * @param {LiveMapJob} job
 * @returns {SimulatedTruckPlan | null} Null when the job has no mappable sites.
 */
export function createTruckPlan(
  uid: string,
  job: LiveMapJob
): SimulatedTruckPlan | null {
  const random = createSeededRandom(uid);
  const firstSite = job.sites[0];
  const lastSite = job.sites[job.sites.length - 1];
  if (!firstSite || !lastSite) return null;
  const virtualOriginBearing = random() * 2 * Math.PI;
  const load =
    firstSite === lastSite
      ? offsetLatLng(
          firstSite,
          Math.sin(virtualOriginBearing) * 9000,
          Math.cos(virtualOriginBearing) * 9000
        )
      : firstSite;
  const plan: SimulatedTruckPlan = {
    uid,
    jobId: job.id,
    load,
    dump: lastSite,
    laneOffsetMeters: 250 + random() * 350,
    speedMps: 13 + random() * 5, // ~29-40 mph
    loadDwellMs: (4 + random() * 4) * 60000,
    dumpDwellMs: (3 + random() * 3) * 60000,
    phaseMs: 0,
    parkingJitterMeters: {
      east: (random() - 0.5) * 60,
      north: (random() - 0.5) * 60,
    },
  };
  plan.phaseMs = random() * getCycleDurationMs(plan);
  return plan;
}

/**
 * Travel time for one leg of the plan.
 * @param {SimulatedTruckPlan} plan
 * @returns {number} Milliseconds.
 */
function getLegDurationMs(plan: SimulatedTruckPlan): number {
  // Curved legs are a bit longer than the straight-line distance
  return (
    ((getDistanceMeters(plan.load, plan.dump) * 1.08) / plan.speedMps) * 1000
  );
}

/**
 * Length of a full load -> dump -> load loop, including time spent at each site.
 * @param {SimulatedTruckPlan} plan
 * @returns {number} Milliseconds.
 */
export function getCycleDurationMs(plan: SimulatedTruckPlan): number {
  return plan.loadDwellMs + plan.dumpDwellMs + 2 * getLegDurationMs(plan);
}

/**
 * Where the truck is at a given point of the simulated clock.
 * @param {SimulatedTruckPlan} plan
 * @param {number} clockMs - Simulated milliseconds since the simulation started.
 * @param {number} timestamp - Real epoch milliseconds stamped on the fix.
 * @returns {GeotrackPoint}
 */
export function getSimulatedPoint(
  plan: SimulatedTruckPlan,
  clockMs: number,
  timestamp: number
): GeotrackPoint {
  const cycleMs = getCycleDurationMs(plan);
  const legMs = getLegDurationMs(plan);
  // Positive modulo so clocks before the start still land inside the cycle
  let elapsed = (((clockMs + plan.phaseMs) % cycleMs) + cycleMs) % cycleMs;
  const parked = (site: LatLngLiteral): GeotrackPoint => ({
    ...offsetLatLng(
      site,
      plan.parkingJitterMeters.east,
      plan.parkingJitterMeters.north
    ),
    timestamp,
    heading: null,
    isMoving: false,
  });
  const driving = (
    from: LatLngLiteral,
    to: LatLngLiteral,
    fraction: number
  ): GeotrackPoint => {
    const position = getPointOnLeg(from, to, plan.laneOffsetMeters, fraction);
    const ahead = getPointOnLeg(
      from,
      to,
      plan.laneOffsetMeters,
      Math.min(1, fraction + 0.01)
    );
    return {
      ...position,
      timestamp,
      heading: getBearingDegrees(position, ahead),
      isMoving: true,
    };
  };

  if (elapsed < plan.loadDwellMs) return parked(plan.load);
  elapsed -= plan.loadDwellMs;
  if (elapsed < legMs) return driving(plan.load, plan.dump, elapsed / legMs);
  elapsed -= legMs;
  if (elapsed < plan.dumpDwellMs) return parked(plan.dump);
  elapsed -= plan.dumpDwellMs;
  return driving(plan.dump, plan.load, Math.min(1, elapsed / legMs));
}

/**
 * Backfill fixes for the recent past so trails exist as soon as simulation starts.
 * @param {SimulatedTruckPlan} plan
 * @param {number} startMs - Real epoch milliseconds the simulation clock is anchored to.
 * @param {number} nowMs - Real epoch milliseconds.
 * @param {number} durationMs - How far back to backfill, in real milliseconds.
 * @param {number} stepMs - Real milliseconds between fixes.
 * @returns {GeotrackPoint[]} Oldest to newest.
 */
export function getSimulatedHistory(
  plan: SimulatedTruckPlan,
  startMs: number,
  nowMs: number,
  durationMs: number,
  stepMs: number
): GeotrackPoint[] {
  const points: GeotrackPoint[] = [];
  for (let t = nowMs - durationMs; t <= nowMs; t += stepMs) {
    points.push(getSimulatedPoint(plan, (t - startMs) * SIMULATION_SPEEDUP, t));
  }
  return points;
}

/**
 * Stand-in operators for jobs that have nobody assigned yet, so the simulation
 * still has trucks to move on an otherwise empty day.
 * @param {LiveMapJob[]} jobs
 * @param {LiveMapOperator[]} operators - Real operators already on the map.
 * @returns {LiveMapOperator[]}
 */
export function createStandInOperators(
  jobs: LiveMapJob[],
  operators: LiveMapOperator[]
): LiveMapOperator[] {
  const staffedJobIds = new Set(
    operators.flatMap((operator) =>
      operator.assignments.map((assignment) => assignment.jobId)
    )
  );
  return jobs
    .filter((job) => !staffedJobIds.has(job.id) && job.sites.length > 0)
    .flatMap((job) =>
      Array.from({ length: Math.min(Math.max(job.totalTrucks, 1), 4) }).map(
        (_, idx) => ({
          uid: `sim-${job.id}-${idx + 1}`,
          name: `Simulated truck ${idx + 1}`,
          nickname: `S${idx + 1}`,
          companyName: 'Simulation',
          truckDisplayId: `SIM-${idx + 1}`,
          assignments: [
            {
              assignmentId: `sim-${job.id}-${idx + 1}`,
              jobId: job.id,
              status: 'checkedIn' as const,
              taskId: null,
            },
          ],
          isSimulated: true,
        })
      )
    );
}
